import { useState } from 'react';
import { Button, cn } from '@zafroi/ui-kit';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import type { BotUsage } from '@/types/engage';

/**
 * Conditional banner shown above the page when a non-BETA tenant is at or
 * over their plan's monthly turn budget. Soft-warn only — the bot keeps
 * answering customers regardless.
 *
 * Dismissal is session-only (component state); the banner re-appears on the
 * next mount until usage drops back into the `under` band.
 */

export interface UsageBannerProps {
  usage: BotUsage;
  onUpgrade: () => void;
}

export function UsageBanner({ usage, onUpgrade }: UsageBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  // The strip handles unlimited / `under` tenants — banner only renders for
  // explicit warning states.
  if (dismissed) return null;
  if (usage.plan.tier === 'BETA') return null;
  if (usage.plan.includedBotTurns == null) return null;
  if (usage.usage.status === 'under') return null;

  const { status, botTurnsThisMonth } = usage.usage;
  const included = usage.plan.includedBotTurns;
  const over = Math.max(0, botTurnsThisMonth - included);

  const config = (() => {
    switch (status) {
      case 'approaching':
        return {
          icon: Info,
          tone: 'bg-warning/10 border-warning/40 text-warning-foreground',
          iconClass: 'text-warning',
          title:
            "You've used 80% of your bot's monthly conversations.",
          body:
            "Upgrade now and you won't run into overage charges.",
          sub: null as string | null,
          cta: 'Upgrade plan',
        };
      case 'over':
        return {
          icon: AlertCircle,
          tone: 'bg-warning/15 border-warning/60 text-warning-foreground',
          iconClass: 'text-warning',
          title: "You've exceeded your plan.",
          body:
            'Each extra customer message is now ₹1 — your bot keeps working for your customers.',
          sub: `Total this month: ${botTurnsThisMonth.toLocaleString()} turns, ${over.toLocaleString()} over.`,
          cta: 'Upgrade plan',
        };
      case 'hard_cap':
      default:
        return {
          icon: AlertTriangle,
          tone: 'bg-destructive/10 border-destructive/40 text-foreground',
          iconClass: 'text-destructive',
          title: "You're 20%+ over your plan.",
          body:
            'Please upgrade to avoid surprise bills — your bot will keep answering customers in the meantime.',
          sub: `Total this month: ${botTurnsThisMonth.toLocaleString()} turns, ${over.toLocaleString()} over.`,
          cta: 'Upgrade plan',
        };
    }
  })();

  const Icon = config.icon;

  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3',
        config.tone,
      )}
    >
      <Icon
        className={cn('mt-0.5 h-5 w-5 shrink-0', config.iconClass)}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">
          {config.title}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
          {config.body}
        </p>
        {config.sub && (
          <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            {config.sub}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button size="sm" onClick={onUpgrade}>
          {config.cta}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
